import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { PersonRemove } from "@mui/icons-material";
import { removeClubMember } from "./api";
import type { ClubMember } from "./types";
import DialogHeader from "./DialogHeader";

interface RemoveMemberDialogProps {
  target: { clubId: number; member: ClubMember } | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const RemoveMemberDialog: React.FC<RemoveMemberDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    if (!target) return;
    try {
      setRemoving(true);
      await removeClubMember(target.clubId, target.member.handler_id);
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error removing club member:", err);
      onError("Failed to remove member. Please try again.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog
      open={target != null}
      onClose={() => !removing && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Remove Member"
        icon={<PersonRemove color="error" />}
        onClose={onClose}
        closeDisabled={removing}
      />
      <DialogContent dividers>
        <Typography variant="body1">
          Are you sure you want to remove{" "}
          <strong>{target?.member.handler_name}</strong> from the club?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          They will lose access to the club and its training groups. This
          cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={removing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={
            removing ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <PersonRemove />
            )
          }
          onClick={handleRemove}
          disabled={removing}
        >
          {removing ? "Removing…" : "Remove Member"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveMemberDialog;
